import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { EyeOff, Pencil, Plus, Trash2 } from "lucide-react";

import {
  AdminPage,
  Loading,
  SortableCard,
  SortableList,
  useRows,
  type Row,
} from "@/components/admin/AdminKit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export const Route = createFileRoute("/_authenticated/admin/teme")({
  component: ThemesAdmin,
});

type ThemeForm = {
  id?: string;
  title: string;
  description: string;
  visible: boolean;
  sort_order: number;
};

function ThemesAdmin() {
  const { rows, isLoading, save, remove, reorder } = useRows("debate_themes");
  const [editing, setEditing] = useState<ThemeForm | null>(null);

  if (isLoading) return <Loading />;

  return (
    <AdminPage
      title="Teme de dezbatere"
      description="Trage cardurile pentru a le reordona. Click pe Editează pentru modificări."
      action={
        <Button
          onClick={() =>
            setEditing({ title: "", description: "", visible: true, sort_order: rows.length })
          }
        >
          <Plus className="mr-2 h-4 w-4" /> Adaugă temă
        </Button>
      }
    >
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nicio temă adăugată încă.</p>
      ) : (
        <SortableList items={rows} onReorder={reorder}>
          {(item: Row) => (
            <SortableCard key={item.id} id={item.id}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold">{String(item["title"] ?? "")}</p>
                    {!item["visible"] && (
                      <span className="flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                        <EyeOff className="h-3 w-3" /> ascuns
                      </span>
                    )}
                  </div>
                  {Boolean(item["description"]) && (
                    <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">
                      {String(item["description"])}
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      setEditing({
                        id: item.id,
                        title: String(item["title"] ?? ""),
                        description: String(item["description"] ?? ""),
                        visible: Boolean(item["visible"]),
                        sort_order: Number(item["sort_order"] ?? 0),
                      })
                    }
                  >
                    <Pencil className="mr-2 h-3.5 w-3.5" /> Editează
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label="Șterge"
                    onClick={() => {
                      if (window.confirm(`Ștergi tema „${String(item["title"] ?? "")}”?`))
                        void remove(item.id);
                    }}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>
            </SortableCard>
          )}
        </SortableList>
      )}

      <Dialog open={editing !== null} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Editează tema" : "Temă nouă"}</DialogTitle>
            <DialogDescription>
              Temele apar pe pagina principală și în secțiunea Tematică.
            </DialogDescription>
          </DialogHeader>

          {editing && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Titlu</Label>
                <Input
                  value={editing.title}
                  onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Descriere</Label>
                <Textarea
                  rows={4}
                  value={editing.description}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <Switch
                  checked={editing.visible}
                  onCheckedChange={(v) => setEditing({ ...editing, visible: v })}
                />
                Vizibil pe site
              </label>
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>
              Anulează
            </Button>
            <Button
              onClick={async () => {
                if (!editing) return;
                const ok = await save({ ...editing });
                if (ok) setEditing(null);
              }}
            >
              Salvează
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminPage>
  );
}
